/**
 * FamilyCard — Parent manages linked minors on their SBT.
 * Shows on GuestPortalPage for age-verified adult guests.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  Users, Plus, Trash2, Loader2, Baby, ShieldAlert, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

const CURRENT_YEAR = new Date().getFullYear();

export default function FamilyCard({ parentWallet, parentEmail }) {
  const [minors, setMinors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [dobYear, setDobYear] = useState('');
  const [tcAccepted, setTcAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    if (!parentWallet && !parentEmail) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (parentWallet) params.set('parent_wallet', parentWallet);
      if (parentEmail) params.set('parent_email', parentEmail);
      const r = await fetch(`${BACKEND_URL}/api/sbt/family/my-family?${params}`);
      const d = await r.json();
      setMinors(d.minors || []);
    } catch (e) {
      toast.error('Failed to load family');
    } finally {
      setLoading(false);
    }
  }, [parentWallet, parentEmail]);

  useEffect(() => { refresh(); }, [refresh]);

  const addMinor = async () => {
    if (!parentWallet) { toast.error('Parent wallet not available'); return; }
    if (!name.trim()) { toast.error('Enter minor\'s name'); return; }
    const year = parseInt(dobYear);
    if (!year || year < 1990 || year > CURRENT_YEAR) { toast.error('Enter a valid birth year'); return; }
    if (CURRENT_YEAR - year >= 18) { toast.error('Age 18+ must create their own account'); return; }
    if (!tcAccepted) { toast.error('You must accept the liability terms'); return; }

    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/sbt/family/add-minor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parent_wallet: parentWallet,
          parent_email: parentEmail || null,
          minor_name: name.trim(),
          minor_dob_year: year,
          tc_accepted: tcAccepted,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(`${name} added to your family`);
      setName(''); setDobYear(''); setTcAccepted(false); setShowAdd(false);
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const removeMinor = async (minor) => {
    if (!confirm(`Remove ${minor.name} from your family?`)) return;
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/sbt/family/minor/${minor.id}?parent_wallet=${encodeURIComponent(parentWallet)}`,
        { method: 'DELETE' },
      );
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success('Removed');
      refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!parentWallet && !parentEmail) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="family-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Family</h3>
          {minors.length > 0 && (
            <Badge className="bg-muted text-foreground text-[10px]">{minors.length}</Badge>
          )}
        </div>
        {!showAdd && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowAdd(true)}
            className="h-7 text-xs"
            data-testid="family-add-btn"
          >
            <Plus className="w-3 h-3 mr-1" /> Add minor
          </Button>
        )}
      </div>

      {/* Age-restriction explainer */}
      <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-3 mb-3 flex gap-2" data-testid="family-explainer">
        <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Minors linked here can stay in your room, but <strong>cannot purchase age-restricted items</strong>
          {' '}(alcohol, tobacco, etc.) through getbitsy. Hotel staff will still visually verify IDs for in-person deliveries.
        </p>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 mb-3 space-y-3" data-testid="family-add-form">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold">Add a minor</p>
            <button onClick={() => setShowAdd(false)} data-testid="family-cancel-btn">
              <X className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
              className="h-9 text-sm"
              data-testid="family-name-input"
            />
            <Input
              type="number"
              value={dobYear}
              onChange={(e) => setDobYear(e.target.value)}
              placeholder={`Year of birth (e.g. ${CURRENT_YEAR - 10})`}
              min={1990}
              max={CURRENT_YEAR}
              className="h-9 text-sm"
              data-testid="family-dob-input"
            />
          </div>
          {dobYear && parseInt(dobYear) && (
            <p className="text-[11px] text-muted-foreground">
              Age: <strong>{CURRENT_YEAR - parseInt(dobYear)}</strong>
              {CURRENT_YEAR - parseInt(dobYear) >= 18 && ' — must create own account'}
            </p>
          )}
          <label className="flex items-start gap-2 cursor-pointer" data-testid="family-tc-label">
            <input
              type="checkbox"
              checked={tcAccepted}
              onChange={(e) => setTcAccepted(e.target.checked)}
              className="mt-0.5"
              data-testid="family-tc-checkbox"
            />
            <span className="text-[11px] text-muted-foreground leading-relaxed">
              I certify this minor's age is accurate. I accept legal responsibility for them during stays. Misdeclaration may result in an SBT fraud flag.
            </span>
          </label>
          <Button
            onClick={addMinor}
            disabled={submitting || !name || !dobYear || !tcAccepted}
            size="sm"
            className="w-full h-9"
            data-testid="family-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add minor'}
          </Button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </div>
      ) : minors.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4" data-testid="family-empty">
          No minors linked to your SBT yet.
        </p>
      ) : (
        <div className="space-y-2">
          {minors.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-3 p-2 rounded-lg border border-border/50"
              data-testid={`family-minor-${m.id}`}
            >
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                <Baby className="w-4 h-4 text-blue-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{m.name}</p>
                <p className="text-[11px] text-muted-foreground">
                  Age {m.current_age} · minor · no purchases of restricted items
                </p>
              </div>
              <button
                onClick={() => removeMinor(m)}
                className="text-muted-foreground hover:text-red-500 p-1"
                data-testid={`family-remove-${m.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
